import React, { ChangeEvent, FC, useState } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { IShiftSwapDTO } from 'src/api/dtos';
import ShiftSwapRepository from 'src/api/repositories/ess/v1/shiftSwap.repository';
import { IError } from 'src/api/types';
import { CloseIcon, Modal, SaveIcon, ShiftDropIcon } from 'src/shared/components';
import { DATE_TIME_FORMAT, DAY_COMMA_DATE_FORMAT } from 'src/shared/constants';
import { ShiftSwapStateEnums } from 'src/shared/enums';
import { useTranslate } from 'src/shared/providers';
import { formatTime, getErrorMessage } from 'src/shared/utils';
import { RootState } from 'src/store/types';
import { getTimeFormat } from 'src/store/user/user.selectors';

interface Props {
  shiftScheduleId: number;
  shiftSwap: IShiftSwapDTO;
  startDate: string;
  endDate: string;
  onClose: () => void;
  isPickup?: boolean;
}

export const CalendarShiftSwapControls: FC<Props> = ({
  shiftScheduleId,
  shiftSwap,
  startDate,
  endDate,
  onClose,
  isPickup,
}) => {
  const { t } = useTranslate();

  const { state, shiftDrop, shiftPickup } = shiftSwap;

  const timeFormat = useSelector(getTimeFormat);
  const { settings } = useSelector((state: RootState) => state.user);

  const hasAutoApproveShift = !!settings?.isShiftSwapAutoApproveEnabled;
  const formattedStartTime = startDate ? formatTime(startDate, timeFormat, DATE_TIME_FORMAT) : undefined;
  const formattedEndTime = endDate ? formatTime(endDate, timeFormat, DATE_TIME_FORMAT) : undefined;

  const formattedStartDate = moment(startDate).format(DAY_COMMA_DATE_FORMAT);
  const formattedEndDate = moment(endDate).format(DAY_COMMA_DATE_FORMAT);

  const formarttedDate =
    formattedStartDate === formattedEndDate ? formattedStartDate : `${formattedStartDate} - ${formattedEndDate}`;

  const [note, setNote] = useState((isPickup ? shiftPickup?.pickupNote : shiftDrop?.dropperNote) ?? '');
  const [swapOpen, setSwapOpen] = useState(false);
  const [isCancellingShift, setIsCancellingShift] = useState(false);

  const showSwap = isPickup ? state === ShiftSwapStateEnums.PENDING_SHIFT_DROP : state === ShiftSwapStateEnums.NONE;
  const showSaveAndCancel = isPickup
    ? shiftPickup && state === ShiftSwapStateEnums.PENDING_PICKUP_APPROVAL
    : shiftDrop &&
      (state === ShiftSwapStateEnums.PENDING_SHIFT_DROP || state === ShiftSwapStateEnums.PENDING_PICKUP_APPROVAL);
  const isNoteEdited = isPickup ? (shiftPickup?.pickupNote ?? '') !== note : (shiftDrop?.dropperNote ?? '') !== note;

  const [createShiftDrop, { isLoading: isCreatingDrop }] = ShiftSwapRepository.endpoints.useCreateShiftDropMutation();
  const [cancelShiftDrop, { isLoading: isCancelingDrop }] = ShiftSwapRepository.endpoints.useCancelShiftDropMutation();
  const [updateShiftDrop, { isLoading: isUpdatingDrop }] = ShiftSwapRepository.endpoints.useUpdateShiftDropMutation();
  const [createShiftPickup, { isLoading: isCreatingPickup }] =
    ShiftSwapRepository.endpoints.useCreateShiftPickupMutation();
  const [cancelShiftPickup, { isLoading: isCancelingPickup }] =
    ShiftSwapRepository.endpoints.useCancelShiftPickupMutation();
  const [updateShiftPickup, { isLoading: isUpdatingPickup }] =
    ShiftSwapRepository.endpoints.useUpdateShiftPickupMutation();

  const loading = isUpdatingDrop || isUpdatingPickup;
  const loadingModalButtons = isCreatingDrop || isCancelingDrop || isCreatingPickup || isCancelingPickup;

  const handleNoteChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNote(event.target.value);
  };

  const handleSwapOpen = (isCancellingShift?: boolean) => {
    setSwapOpen(true);
    if (isCancellingShift) {
      setIsCancellingShift(true);
    }
  };

  const handleDropOpen = (isCancellingShift?: boolean) => {
    setSwapOpen(false);
    if (isCancellingShift) {
      setIsCancellingShift(false);
    }
  };

  const handleSwapConfirm = async () => {
    try {
      if (isPickup) {
        if (!shiftDrop) {
          return;
        }

        await createShiftPickup({
          shiftDropId: shiftDrop.shiftDropId,
          note: note || null,
        }).unwrap();

        toast.info(
          hasAutoApproveShift
            ? t('shiftSwaps.youHaveSuccessfullyAddedTheScheduleTime', {
                time: `${formattedStartTime} - ${formattedEndTime}`,
                date: formarttedDate,
              })
            : t('shiftSwaps.youHaveSuccessfullyRequestedShiftTimeOnDate', {
                time: `${formattedStartTime} - ${formattedEndTime}`,
                date: formarttedDate,
              }),
          { type: toast.TYPE.SUCCESS },
        );
      } else {
        await createShiftDrop({
          shiftScheduleId,
          note: note || null,
        }).unwrap();

        toast.info(t('shiftSwaps.youHaveSuccessfullySubmittedADropRequest'), { type: toast.TYPE.SUCCESS });
      }
      onClose();
    } catch (error) {
      const errorMessage = getErrorMessage(error as IError, t('global.oopsAnErrorOccurred'));
      toast.info(errorMessage, { type: toast.TYPE.ERROR });
    }
  };

  const handleSwapCancel = async () => {
    try {
      if (isPickup) {
        if (!shiftPickup) {
          return;
        }

        await cancelShiftPickup({
          shiftPickupId: shiftPickup.shiftPickupId,
        }).unwrap();

        toast.info(t('shiftSwaps.youHaveSuccessfullyCanceledRequestingShift'), { type: toast.TYPE.SUCCESS });
      } else {
        if (!shiftDrop) {
          return;
        }

        await cancelShiftDrop({
          shiftDropId: shiftDrop.shiftDropId,
        }).unwrap();

        toast.info(
          t('shiftSwaps.youHaveSuccessfullyCancelledADroppedShiftTimeOnDate', {
            time: `${formattedStartTime} - ${formattedEndTime}`,
            date: formarttedDate,
          }),
          { type: toast.TYPE.SUCCESS },
        );
      }
      onClose();
    } catch (error) {
      const errorMessage = getErrorMessage(error as IError, t('global.oopsAnErrorOccurred'));
      toast.info(errorMessage, { type: toast.TYPE.ERROR });
    }
  };

  const handleSaveNote = async () => {
    try {
      if (isPickup) {
        if (!shiftPickup) {
          return;
        }

        await updateShiftPickup({
          shiftPickupId: shiftPickup.shiftPickupId,
          note: note || null,
        }).unwrap();

        toast.info(t('shiftSwaps.youHaveSuccessfullyUpdatedTheShiftPickupNote'), { type: toast.TYPE.SUCCESS });
      } else {
        if (!shiftDrop) {
          return;
        }

        await updateShiftDrop({
          shiftDropId: shiftDrop.shiftDropId,
          note: note || null,
        }).unwrap();

        toast.info(t('shiftSwaps.youHaveSuccessfullyUpdatedTheShiftDropNote'), { type: toast.TYPE.SUCCESS });
      }
      onClose();
    } catch (error) {
      const errorMessage = getErrorMessage(error as IError, t('global.oopsAnErrorOccurred'));
      toast.info(errorMessage, { type: toast.TYPE.ERROR });
    }
  };

  if (state === ShiftSwapStateEnums.DISABLED || state === ShiftSwapStateEnums.SHIFT_PICKED_UP) {
    return null;
  }

  return (
    <>
      <Stack gap={2}>
        {isPickup && (
          <Stack gap={1}>
            <Typography fontWeight={500}>{t('shiftSwaps.droppersNote')}</Typography>
            <Typography fontSize="0.875rem" fontStyle="italic">
              {shiftDrop?.dropperNote ? `"${shiftDrop.dropperNote}"` : '-'}
            </Typography>
          </Stack>
        )}
        <Stack gap={1}>
          <Typography fontWeight={500}>
            {isPickup ? t('shiftSwaps.requestersNote') : t('shiftSwaps.droppersNote')}
          </Typography>
          <TextField value={note} onChange={handleNoteChange} minRows={3} maxRows={3} multiline />
        </Stack>
      </Stack>

      <Stack direction="row" marginTop={4} justifyContent="flex-end" gap={2}>
        {showSwap && (
          <LoadingButton
            variant="contained"
            loading={loading}
            startIcon={<ShiftDropIcon />}
            onClick={() => handleSwapOpen(false)}
          >
            {isPickup ? t('shiftSwaps.requestShift') : t('shiftSwaps.dropShift')}
          </LoadingButton>
        )}
        {showSaveAndCancel && (
          <>
            <LoadingButton
              variant="contained"
              color="secondary"
              loading={loading}
              startIcon={<SaveIcon />}
              onClick={handleSaveNote}
              disabled={!isNoteEdited}
            >
              {t('shiftSwaps.saveNote')}
            </LoadingButton>
            <LoadingButton
              variant="contained"
              loading={loading}
              startIcon={<CloseIcon />}
              onClick={() => handleSwapOpen(true)}
              disabled={isNoteEdited}
            >
              {isPickup ? t('shiftSwaps.cancelShiftPickup') : t('shiftSwaps.cancelShiftDrop')}
            </LoadingButton>
          </>
        )}
      </Stack>
      <DropShiftConfirmationModal
        open={swapOpen}
        startDate={startDate}
        endDate={endDate}
        onSubmit={isCancellingShift ? handleSwapCancel : handleSwapConfirm}
        onClose={handleDropOpen}
        isPickup={isPickup}
        isLoading={loadingModalButtons}
        isCancelling={isCancellingShift}
        hasAutoApproveShift={hasAutoApproveShift}
      />
    </>
  );
};

interface DropShiftConfirmationModalProps {
  open: boolean;
  startDate: string;
  endDate: string;
  onSubmit: () => void;
  onClose: () => void;
  isPickup?: boolean;
  isLoading?: boolean;
  isCancelling?: boolean;
  hasAutoApproveShift?: boolean;
}

const DropShiftConfirmationModal: FC<DropShiftConfirmationModalProps> = ({
  open,
  startDate,
  endDate,
  onSubmit,
  onClose,
  isPickup,
  isLoading,
  isCancelling,
  hasAutoApproveShift,
}) => {
  const { t } = useTranslate();
  const timeFormat = useSelector(getTimeFormat);

  const formattedStartDate = moment(startDate).format(DAY_COMMA_DATE_FORMAT);
  const formattedEndDate = moment(endDate).format(DAY_COMMA_DATE_FORMAT);
  const formattedStartTime = formatTime(startDate, timeFormat, DATE_TIME_FORMAT);
  const formattedEndTime = formatTime(endDate, timeFormat, DATE_TIME_FORMAT);
  const date =
    formattedStartDate === formattedEndDate ? formattedStartDate : `${formattedStartDate} - ${formattedEndDate}`;

  const getShiftMessage = () => {
    if (hasAutoApproveShift && isPickup) {
      return t('shiftSwaps.areYouSureYouWantToPickupThisShift');
    }

    if (isPickup && !isCancelling) {
      return t('shiftSwaps.youAreRequestingAShiftTimeOnDate', {
        date: date,
        time: `${formattedStartTime}-${formattedEndTime}`,
      });
    }

    if (isCancelling && !isPickup) {
      return t('shiftSwaps.areYouSureYouWantToCancelThisShiftDrop');
    }

    if (isCancelling && isPickup) {
      return t('shiftSwaps.areYouSureYouWantToCancelThisShiftPickup');
    }

    return t('shiftSwaps.youAreDroppingAShiftTimeOnDate', {
      date: date,
      time: `${formattedStartTime}-${formattedEndTime}`,
    });
  };

  const getShiftNoteMessage = () => {
    if (isCancelling) return null;

    if (hasAutoApproveShift && isPickup) {
      return t('shiftSwaps.noteByPickingThisUpYouWillBeScheduledToWork');
    }

    if (isPickup) {
      return t('shiftSwaps.noteYouWillNotBeScheduledForThisShiftUnless');
    }

    return t('shiftSwaps.noteEmployeesAreResponsibleToWorkTheirShiftUntilASuitible');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      modalHeaderProps={{
        title: isPickup ? t('shiftSwaps.pickupShift') : t('shiftSwaps.dropShift'),
        onClose: onClose,
      }}
      modalFooterProps={{
        submitText: t('shiftSwaps.confirm'),
        onSubmit: onSubmit,
        closeText: t('global.cancel'),
        loading: isLoading,
        onClose: onClose,
      }}
    >
      <Stack direction="column" gap={3}>
        <Typography>{getShiftMessage()}</Typography>
        <Typography>{!isCancelling && getShiftNoteMessage()}</Typography>
      </Stack>
    </Modal>
  );
};
